import React, { useState, useEffect, useRef } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import Tooltip from '@mui/material/Tooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import TextareaAutosize from '@mui/base/TextareaAutosize';
import AiAssistantsModal from './AiAssistantsModal';
import SaveInsightsModal from './SaveInsightsModal';
import AiSocialShareModal from './AiSocialShareModal';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import DialogContentText from '@mui/material/DialogContentText';
import useMediaQuery from '@mui/material/useMediaQuery';
import FolderIcon from '@mui/icons-material/Folder';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ProjectsInfoModal from './ProjectsInfoModal';
import DiMarkdown from './DiMarkdown';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import SearchIcon from '@mui/icons-material/Search';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ArticleIcon from '@mui/icons-material/Article';

// Add this interface
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function UniversalChat({agent, data} :any) {

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));


  const threadsPerRow = 3;

  const [messages, setMessages] = useState([])
  const [loginRequired, setLoginRequired] = useState(false);
  const [requestTimeout, setRequestTimeout] = useState(false);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [fetchingAnswer, setFetchingAnswer] = useState(false);
  const [sendable, setSendable] = useState(false);
  const [nextThreads, setNextThreads] = useState(threadsPerRow);
  const [serverError, setServerError] = useState(false);
  const [discussionTitle, setDiscussionTitle] = useState(data?.title)
  const [open, setOpen] = React.useState(false);
  const [discussionId, setDiscussionId] = useState(data?.discussion_id)
  const [hoveredThreadId, setHoveredThreadId] = useState(null);
  const [threads, setThreads] = useState(data?.conversation_threads || []);
  const [deleteMessageConfirmOpen, setDeleteMessageConfirmOpen] = useState(false);
  const [deleteThreadConfirmOpen, setDeleteThreadConfirmOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [threadToDelete, setThreadToDelete] = useState(null);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [chatProjects, setChatProjects] = useState([]);

  const handleMoreThreads = () => {
    setNextThreads(nextThreads + threadsPerRow);
  };

  const chatBoxRef = useRef(null);
  const newMessageRef = useRef(null);
  const questionFormRef = useRef(null);
  const newTitleRef = useRef(null);

  const previous_messages_url = "/chat/" + discussionId + "/previous_messages";
  const question_url = "/chat/questions/";
  const update_title_url = "/chat_discussion_threads/" + (discussionId === undefined ? null : discussionId);

  const handleClickOpen = (e) => {
    e.preventDefault();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const ExamplePrompt = ({ prompt }: { prompt: string }) => (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        cursor: loginRequired ? 'not-allowed' : 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': !loginRequired ? {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        } : {}
      }}
      onClick={(e) => !loginRequired && updateSampleMessage(e, prompt)}
    >
      <CardContent sx={{
        p: { xs: 1.5, sm: 2 }, // Responsive padding
        '&:last-child': { pb: { xs: 1.5, sm: 2 } } // Fix for last-child padding
      }}>
        <Box sx={{
          typography: {
            xs: 'body2',
            sm: 'body1'
          }
        }}>
          {prompt}
        </Box>
      </CardContent>
    </Card>
  );

  function LimaInitialMessage() {
    const [tabValue, setTabValue] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
      setTabValue(newValue);
    };

    return (
      <div className="chat-message">
        <style>
          {`
            .chat-message .MuiCard-root:hover {
              background-color: rgba(0, 0, 0, 0.04);
              transition: background-color 0.3s ease;
            }

            .chat-message .MuiTabs-root {
              margin-bottom: 20px;
            }

            .chat-message .MuiCardContent-root {
              padding: 16px;
            }

            .chat-message .MuiTab-root {
              text-transform: none;
              min-height: 48px;
              padding: 6px 12px;
            }

            @media (max-width: 600px) {
              .chat-message .MuiTab-root {
                min-width: auto;
                padding: 6px 8px;
                font-size: 12px;
              }

              .chat-message .MuiTab-wrapper {
                flex-direction: column;
              }

              .chat-message .MuiCardContent-root {
                padding: 12px;
              }

              .chat-message .MuiButton-root {
                padding: 4px 8px;
                font-size: 12px;
              }
            }
          `}
        </style>

        <p>{data?.current_user ? (`Hi ${data.current_user},`) : "Hi,"}</p>
        <p>
          I am an AI Stock Market Research Analyst ready to help you make informed investment decisions.
          Below are some ways I can assist you:
        </p>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="AI Assistant capabilities"
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons={isMobile ? "auto" : false}
            allowScrollButtonsMobile
          >
            <Tab
              icon={isMobile ? null : <AnalyticsIcon />}
              label="Analysis"
              sx={{
                minWidth: isMobile ? 'auto' : 'inherit',
                fontSize: isMobile ? '0.875rem' : 'inherit'
              }}
            />
            <Tab
              icon={isMobile ? null : <CompareArrowsIcon />}
              label="Compare"
              sx={{
                minWidth: isMobile ? 'auto' : 'inherit',
                fontSize: isMobile ? '0.875rem' : 'inherit'
              }}
            />
            <Tab
              icon={isMobile ? null : <SearchIcon />}
              label="Stock Screener"
              sx={{
                minWidth: isMobile ? 'auto' : 'inherit',
                fontSize: isMobile ? '0.875rem' : 'inherit'
              }}
            />
            <Tab
              icon={isMobile ? null : <ArticleIcon />}
              label="SEC Filings"
              sx={{
                minWidth: isMobile ? 'auto' : 'inherit',
                fontSize: isMobile ? '0.875rem' : 'inherit'
              }}
            />
            <Tab
              icon={isMobile ? null : <SmartToyIcon />}
              label="AI Agents"
              sx={{
                minWidth: isMobile ? 'auto' : 'inherit',
                fontSize: isMobile ? '0.875rem' : 'inherit'
              }}
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <p>Get detailed analysis and insights about specific companies (examples):</p>
          <Box sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr',
              md: 'repeat(auto-fill, minmax(400px, 1fr))'
            }
          }}>
            <ExamplePrompt prompt="Summarize Apple's latest earnings" />
            <ExamplePrompt prompt="Analyze Microsoft's income statements for the last 10 years" />
            <ExamplePrompt prompt="What are NVIDIA's expectations for the coming quarter and year?" />
            <ExamplePrompt prompt="Summarize Tesla's last 3 earnings calls" />
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <p>Compare multiple companies to make better investment decisions:</p>
          <Box sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr',
              md: 'repeat(auto-fill, minmax(400px, 1fr))'
            }
          }}>
            <ExamplePrompt prompt="Compare Coca-Cola (KO) with Pepsico's cash flows for the last ten years, which one is more profitable?" />
            <ExamplePrompt prompt="Compare Costco to its top 3 competitors" />
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <p>Use our powerful stock screener to find investment opportunities that match your criteria:</p>
          <Box sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr',
              md: 'repeat(auto-fill, minmax(400px, 1fr))'
            }
          }}>
            <ExamplePrompt prompt="Find S&P 500 companies that pay high dividends" />
            <ExamplePrompt prompt="Find S&P 500 and S&P 400 stocks with value scores > 80 and payback time < 10 years" />
            <ExamplePrompt prompt="Show me US-based stocks with a value score above 80" />
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <p>Get detailed insights from SEC filings and regulatory documents:</p>
          <Box sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr',
              md: 'repeat(auto-fill, minmax(400px, 1fr))'
            }
          }}>
            <ExamplePrompt prompt="What is Apple Inc.'s primary sources of revenue, and how have they changed over the past year from their latest 10-K?" />
            <ExamplePrompt prompt="Summarize the key financial and product highlights from Microsoft's latest 10-Q filing?" />
            <ExamplePrompt prompt="What is the outlook for Costco based on the management discussion from their latest 10-K?" />
            <ExamplePrompt prompt="Summarize NVIDIA's 2023 10-k" />
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <p>Let our AI Agents handle complex research tasks and keep you updated:</p>
          <Box sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr',
              md: 'repeat(auto-fill, minmax(400px, 1fr))'
            }
          }}>
            <ExamplePrompt prompt="Monitor Apple stock and notify me of any important events and red flags" />
            <ExamplePrompt prompt="Monitor Apple, Google, Microsoft, and Meta stock. Notify me if any of them start trending toward being undervalued" />
            <ExamplePrompt prompt="Create a project to determine if I should buy NVIDIA stock" />
            <ExamplePrompt prompt="Create a project to perform a DCF valuation on Apple stock" />
          </Box>
        </TabPanel>

        <Box sx={{ mt: 3 }}>
          <p>Learn more about the <AiAssistantsModal message="AI Assistants"/> and <ProjectsInfoModal message="Agents"/>.</p>
        </Box>
      </div>
    );
  }

  function agentOnlineColor(agent_name) {
    if (agent_name == 'Lima') {
      return 'dot-indicator sm bg-success'
    } else if (agent_name == 'Saturday') {
      return 'dot-indicator sm bg-primary'
    } else if (agent_name == 'June') {
      return 'dot-indicator sm bg-info'
    } else {
      return 'dot-indicator sm bg-warning'
    }
  }

  useEffect(() => {
    fetch(previous_messages_url)
      .then(function(response) {
        if (response.status === 401) {
          setLoginRequired(true);
          return [];
        } else if (response.status === 200) {
          setLoginRequired(false);
        }
        return response.json();
      })
      .then(function(data) {
        setMessages(data);
        (data.length > 0) ? chatBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }) : null
      })
      .catch(error => {console.log(error?.message); console.log('something went wrong');})

    // Fetch chat projects
    if (discussionId) {
      fetch(`/chat/${discussionId}/projects`)
        .then(response => response.json())
        .then(data => setChatProjects(data))
        .catch(error => console.error('Error fetching chat projects:', error));
    }
  }, [discussionId])

  function updateMessages(e) {
    e.preventDefault();
    const message = newMessageRef.current?.value;
    if (message === '') return null;
    setFetchingAnswer(true);
    setMessages([...messages, {role: "user", content: message}]);
    newMessageRef.current.value = "";
    setSendable(false);
    fetchAnswer(message);
  }

  function updateSampleMessage(e, message) {
    e.preventDefault();
    if (message === '') return null;
    setFetchingAnswer(true);
    chatBoxRef.current?.scrollIntoView({ behavior: 'smooth', inline: 'end' });
    setMessages([...messages, {role: "user", content: message}]);
    newMessageRef.current.value = "";
    setSendable(false);
    fetchAnswer(message);
  }

  function fetchAnswer(question) {
    fetch(question_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({question: question, discussion_id: (discussionId == undefined ? null : discussionId), project_ids: chatProjects.map(project => project.id)})
    })
    .then(function(response) {
      if (response.status === 401) {
        setLoginRequired(true);
      } else if (response.status === 403) {
        setQuotaExceeded(true);
        setFetchingAnswer(false);
        return null;
      } else if (response.status === 504) {
        setFetchingAnswer(false);
        setRequestTimeout(true);
        return null;
      } else if (response.status === 500) {
        setFetchingAnswer(false);
        setServerError(true);
        return null;
      } else if (response.status === 200) {
        setLoginRequired(false);
      }
      return response.json();
    })
    .then(function(data) {
      if (data === null) return null;
      chatBoxRef.current?.scrollIntoView({ behavior: 'smooth', inline: 'end' });
      setMessages(
        [
          ...messages,
          {role: "user", content: question, pair_id: data.pair_id},
          {role: "assistant", content: data.answer, pair_id: data.pair_id, title: data.title, public_url: data.public_url, unique_id: data.unique_id}
        ]
      );
      setFetchingAnswer(false);
      if (discussionId === undefined) {
        window.history.pushState(
          'AIStockMarketResearchAnalyst',
          'AI Stock Market Research Analyst | Instant Market Insights | Decode Investing',
          "/chat/" + data.discussion_id
        );

        setDiscussionId(data.discussion_id);
        setDiscussionTitle(data.title);
      }
    })
    .catch(error => {console.log(error?.message); console.log('something went wrong');})
  }

  function checkSendable() {
    if (newMessageRef.current?.value === '') {
      setSendable(false);
    } else {
      setSendable(true);
    }
  }

  function onEnterPress(e) {
    if(e.keyCode == 13 && e.shiftKey == false) {
      updateMessages(e);
    }
  }

  function refreshPage(e) {
    e.preventDefault();
    location.reload();
  }

  function copyMessage(e, content) {
    navigator.clipboard.writeText(content);
  }

  function deleteMessage(e, pair_id) {
    e.preventDefault();
    setMessageToDelete(pair_id);
    setDeleteMessageConfirmOpen(true);
  }

  function confirmDeleteMessage() {
    const list = messages.filter(i => i.pair_id !== messageToDelete);
    const url = "/gpt_company_messages/" + messageToDelete;
    setMessages(list);
    fetch(url, {method: 'DELETE'})
      .then(resp => resp.json)
      .then(_data => console.log('deleted'))
    setDeleteMessageConfirmOpen(false);
  }

  function downloadMessage(e, content) {
    const element = document.createElement("a");
    const file = new Blob([content], {type: 'text/plain'});
    element.href = URL.createObjectURL(file);
    element.download = `${content.slice(0, 20)}_analysis_of_${data.ticker}_generated_by_${agent}_ai_assistant.txt`;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  }

  function openchat(e, url) {
    window.location.replace(url);
  }

  function handleDefaultAgent(e, agent) {
    localStorage.setItem('defaultAgents', JSON.stringify([agent]));
  }

  function renameTitle(e) {
    e.preventDefault();
    const newTitle = newTitleRef.current?.value;
    if (newTitle === '') return null;
    updateTitle(newTitle)
    setOpen(false);
  }

  function updateTitle(newTitle) {
    fetch(update_title_url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({title: newTitle})
    })
    .then(function(response) {
      if (response.status === 200) {
        setDiscussionTitle(newTitle)
      }

      return response.json();
    })
  }

  const handleDeleteThread = (e, threadId) => {
    e.stopPropagation();
    setThreadToDelete(threadId);
    setDeleteThreadConfirmOpen(true);
  };

  const confirmDeleteThread = () => {
    fetch(`/chat_discussion_threads/${threadToDelete}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then(response => {
      if (response.ok) {
        setThreads(prevThreads => prevThreads.filter(thread => thread.id !== threadToDelete));
      } else {
        console.error('Failed to delete thread');
      }
    })
    .catch(error => {
      console.error('Error:', error);
    });
    setDeleteThreadConfirmOpen(false);
  };

  const handleProjectModalOpen = () => {
    setProjectModalOpen(true);
    // Fetch projects from backend

    fetch('/projects')
      .then(response => response.json())
      .then(data => {
        // Use all projects without filtering
        setProjects(data);
      });

  };

  const handleProjectModalClose = () => {
    setProjectModalOpen(false);
  };

  const handleProjectSelect = (event) => {
    setSelectedProject(event.target.value);
  };

  const handleAddProject = () => {
    if (selectedProject) {
      // Check if the project is already in chatProjects
      if (chatProjects.some(project => project.id === selectedProject)) {
        handleProjectModalClose();
        return;
      }

      fetch(`/chat/${discussionId}/add_project`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ project_id: selectedProject }),
      })
      .then (function(response) {
        if (response.status === 500) {
          return null;
        }
        return response.json();
      })
      .then(_data => {
        const addedProject = projects.find(p => p.id === selectedProject);
        if (addedProject) {
          setChatProjects([...chatProjects, addedProject]);
        }
        handleProjectModalClose();

      })
      .catch(error => console.error('Error:', error));
    }
  };

  const handleRemoveProject = (projectId) => {
    fetch(`/chat/${discussionId}/remove_project`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ project_id: projectId }),
    })
      .then(response => response.json())
      .then(_data => {
        setChatProjects(chatProjects.filter(project => project.id !== projectId));
      })
      .catch(error => console.error('Error:', error));
  };

  return (
    <div>
      <div className="page-header">
        <h3 className="page-title"></h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item active" aria-current="page">AI Chat</li>
          </ol>
        </nav>
      </div>
      <div className="row">
        <div className="col-md-12">
          <div className="card chat-app-wrapper">
            <div className="row mx-0">
              <div className="col-lg-3 col-md-4 chat-list-wrapper px-0">
                <div className="sidebar-spacer">
                  <div className="px-4">
                    <div className="d-flex pt-4">
                      <div className="wrapper">
                        <h5 className="font-weight-medium mb-0 ellipsis">{ (discussionTitle === undefined) ? 'Untitled Chat' : discussionTitle }</h5>
                          <span>
                            <Tooltip title="New Chat">
                              <IconButton href="/chat" size="small" aria-label="Rename">
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Rename Chat">
                              <IconButton onClick={(e) => handleClickOpen(e) } size="small" aria-label="Rename">
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Dialog fullScreen={fullScreen} onClose={handleClose} open={open}>
                              <DialogTitle>Rename Chat</DialogTitle>
                              <DialogContent>
                                <DialogContentText>
                                  Enter a new title for this chat.
                                </DialogContentText>
                                <TextField
                                  inputRef={newTitleRef}
                                  id="new-chat-title-textfield"
                                  required
                                  autoFocus
                                  margin="dense"
                                  label="New Title"
                                  fullWidth
                                  variant="standard"
                                />
                              </DialogContent>
                              <DialogActions>
                                <Button size="small" onClick={handleClose}>Cancel</Button>
                                <Button size="small" onClick={(e) => renameTitle(e)}>Submit</Button>
                              </DialogActions>
                            </Dialog>
                            <Tooltip title="Add Project">
                              <IconButton onClick={handleProjectModalOpen} size="small" aria-label="Add Project">
                                <FolderIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </span>
                        <p className="mb-0 text-muted ellipsis">AI Chat</p>
                      </div>
                    </div>
                    {chatProjects.length > 0 && (
                      <div className="mt-2">
                        <strong>Projects: </strong>
                        {chatProjects.map((project) => (
                          <span key={project.id} className="project-tag">
                            {project.title}
                            <Tooltip title="Remove Project">
                              <IconButton
                                onClick={() => handleRemoveProject(project.id)}
                                size="small"
                                aria-label="Remove Project"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="chat-list-item-wrapper">
                  {threads.slice(0, nextThreads)?.map((thread) => (
                    <div
                      key={thread.id}
                      onClick={(e) => openchat(e, thread.url)}
                      className="list-item"
                      onMouseEnter={() => setHoveredThreadId(thread.id)}
                      onMouseLeave={() => setHoveredThreadId(null)}
                      style={{ position: 'relative' }}
                    >
                      <div className="profile-image">
                        <div className={agentOnlineColor(thread.agent)}></div>
                      </div>
                      <p className="user-name">{thread.agent}</p>
                      <p className="chat-time">{thread.date}</p>
                      <p style={{ color: '#7e7e7e' }} className="chat-text">{thread.header}</p>
                      <p>
                        {thread.message_type === 'universal_chat' && hoveredThreadId === thread.id && (
                          <Tooltip title="Delete">
                            <IconButton
                              onClick={(e) => handleDeleteThread(e, thread.id)}
                              size="small"
                              aria-label="Delete"
                              style={{ position: 'absolute', right: '10px', top: '80%', transform: 'translateY(-50%)' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </p>
                    </div>
                  ))}
                </div>
                { nextThreads < threads.length ? (
                  <div className="sidebar-spacer">
                    <button onClick={handleMoreThreads} className="btn btn-block btn-gradient-success py-3" type="button">+ More</button>
                  </div>
                ) : null }
              </div>
              <div className="col-lg-7 col-md-8 px-0 d-flex flex-column">
                <div className="chat-container-wrapper">

                  <div className="chat-bubble incoming-chat">
                    { LimaInitialMessage() }
                    <div className="sender-details">
                      <img className="sender-avatar img-xs rounded-circle" src="/images/lima_avatar.jpg" alt="profile image" />
                      <p className="seen-text">Lima (AI Assistant)</p>
                    </div>
                  </div>

                  {messages.map((message =>
                    <div className={(message.role == "user") ? "chat-bubble outgoing-chat" : "chat-bubble incoming-chat" }>
                      <div className="chat-message">
                        <DiMarkdown>{message.content}</DiMarkdown>
                        <p className="font-weight-medium">
                        {(message.role == "assistant" && message.pair_id !== null) ? (
                            <Tooltip title="Delete">
                              <IconButton onClick={(e) => deleteMessage(e, message.pair_id) } size="small" aria-label="Delete">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {(message.role == "assistant") ? (
                            <span>
                              <Tooltip title="Copy">
                                <IconButton onClick={(e) => copyMessage(e, message.content) } size="small" aria-label="Copy">
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download">
                                <IconButton onClick={(e) => downloadMessage(e, message.content) } size="small" aria-label="Download">
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </span>
                          ) : null}
                          {(message.role == "assistant" && message.pair_id !== null) ? (
                            <AiSocialShareModal message={message} />
                          ) : null}
                          {(message.role == "assistant" && message.pair_id !== null) ? (
                            <SaveInsightsModal message={message} />
                          ) : null}
                        </p>
                      </div>
                      <div className="sender-details">
                        {message.role == "assistant" ? (
                          <>
                            <img className="sender-avatar img-xs rounded-circle" src="/images/lima_avatar.jpg" alt="profile image" />
                            <p className="seen-text">Lima (AI Assistant)</p>
                          </>
                        ) : null}
                      </div>
                    </div>
                  ))}
                  <span ref={chatBoxRef}></span>
                  {fetchingAnswer ? (
                    <div className="dot-opacity-loader">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  ) : null}
                  {requestTimeout ? (
                    <p className='card-description'>
                      <div className="alert alert-danger" role="alert">Click <a href='#' onClick={ (e) =>  refreshPage(e)  }>here</a> to see the results or refresh the page.</div>
                    </p>
                  ) : null}
                  {serverError ? (
                    <p className='card-description'>
                      <div className="alert alert-danger" role="alert">Sorry, something went wrong! Refresh the page or click <a href='#' onClick={ (e) =>  refreshPage(e)  }>here</a> and try again.</div>
                    </p>
                  ) : null}
                  {quotaExceeded ? (
                    <div className="quota-exceeded-prompt">
                      <style>
                        {`
                          .quota-exceeded-prompt {
                            padding: 2rem;
                            text-align: center;
                            background: linear-gradient(to right bottom, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.98));
                            border-radius: 12px;
                            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
                            margin: 1rem auto;
                            max-width: 450px;
                            border: 1px solid rgba(237, 137, 54, 0.2);
                          }

                          .quota-icon {
                            font-size: 48px;
                            color: #ed8936;
                            margin-bottom: 1rem;
                          }

                          .quota-title {
                            color: #2d3748;
                            font-size: 1.5rem;
                            font-weight: 600;
                            margin-bottom: 0.75rem;
                          }

                          .quota-message {
                            color: #718096;
                            margin-bottom: 1.5rem;
                            font-size: 1rem;
                            line-height: 1.6;
                          }

                          .quota-actions {
                            display: flex;
                            flex-direction: column;
                            gap: 0.75rem;
                            align-items: center;
                          }

                          .upgrade-button {
                            display: inline-flex;
                            align-items: center;
                            gap: 0.5rem;
                            padding: 0.75rem 1.5rem;
                            background: linear-gradient(135deg, #ed8936, #dd6b20);
                            color: white;
                            text-decoration: none;
                            border-radius: 8px;
                            font-weight: 500;
                            transition: all 0.2s ease;
                            border: none;
                            cursor: pointer;
                          }

                          .upgrade-button:hover {
                            transform: translateY(-1px);
                            box-shadow: 0 4px 12px rgba(237, 137, 54, 0.25);
                            text-decoration: none;
                            color: white;
                          }

                          .view-usage {
                            color: #718096;
                            text-decoration: none;
                            font-size: 0.9rem;
                            transition: color 0.2s ease;
                          }

                          .view-usage:hover {
                            color: #2d3748;
                            text-decoration: underline;
                          }
                        `}
                      </style>
                      <div className="quota-icon">
                        <i className="mdi mdi-alert-circle-outline"></i>
                      </div>
                      <h4 className="quota-title">Usage Limit Reached</h4>
                      <p className="quota-message">
                        You've reached your limit of questions/tasks with {agent} AI Analyst. Upgrade now to continue getting valuable market insights.
                      </p>
                      <div className="quota-actions">
                        <a href="/pricing" className="upgrade-button">
                          <i className="mdi mdi-rocket-launch-outline"></i>
                          Upgrade to Continue
                        </a>
                        <a href="/dashboard" className="view-usage">
                          View my usage details
                        </a>
                      </div>
                    </div>
                  ) : null}
                  {loginRequired ? (
                    <div className="login-prompt">
                      <style>
                        {`
                          .login-prompt {
                            padding: 1.5rem;
                            text-align: center;
                            background: rgba(255, 255, 255, 0.9);
                            border-radius: 8px;
                            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                            margin: 0.5rem auto 1rem;
                            max-width: 400px;
                            position: relative;
                            bottom: 0;
                          }

                          .login-prompt h4 {
                            color: #2c3e50;
                            margin-bottom: 0.75rem;
                            font-weight: 500;
                            font-size: 1.1rem;
                          }

                          .login-prompt p {
                            color: #7f8c8d;
                            margin-bottom: 1rem;
                            font-size: 0.9rem;
                          }

                          .login-button {
                            display: inline-block;
                            padding: 8px 20px;
                            background: #3498db;
                            color: white;
                            text-decoration: none;
                            border-radius: 6px;
                            font-weight: 500;
                            transition: all 0.2s ease;
                          }

                          .login-button:hover {
                            background: #2980b9;
                            transform: translateY(-1px);
                            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                            color: white;
                            text-decoration: none;
                          }
                        `}
                      </style>
                      <h4>Sign in Required</h4>
                      <p>Please sign in to continue using the AI assistant</p>
                      <a href="/signin" className="login-button">
                        Sign In
                      </a>
                    </div>
                  ) : (
                    <div className="mt-auto">
                      <form ref={questionFormRef} action="#" className="pt-4">
                        <div className="input-group">
                          <TextareaAutosize minRows={2} className="form-control" onKeyDown={(e) => onEnterPress(e)} onChange={ checkSendable } ref={newMessageRef} placeholder="Ask a question"/>
                          <div className="input-group-append">
                            <button type="submit"  onClick={(e) => updateMessages(e)} className="input-group-text">
                              <i className={sendable ? "mdi text-success mdi-send icon-sm" : "mdi mdi-send icon-sm" }>
                              <SendIcon fontSize='small' />
                              </i>
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Add these dialogs near the end of the component */}
      <Dialog open={deleteMessageConfirmOpen} onClose={() => setDeleteMessageConfirmOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this message?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteMessageConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteMessage} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteThreadConfirmOpen} onClose={() => setDeleteThreadConfirmOpen(false)}>
        <DialogTitle>Confirm Thread Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this chat thread?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteThreadConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteThread} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
      {/* Add Project Modal */}
      <Dialog open={projectModalOpen} onClose={handleProjectModalClose}>
        <DialogTitle>Add Project to Chat</DialogTitle>
        <DialogContent>
          <FormControl fullWidth>
            <InputLabel id="project-select-label">Select Project</InputLabel>
            <Select
              labelId="project-select-label"
              value={selectedProject}
              onChange={handleProjectSelect}
            >
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>{project.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleProjectModalClose}>Cancel</Button>
          <Button onClick={handleAddProject}>Add to Chat</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default UniversalChat;
